import type { BeneficiaryListItemPayload } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiariesFetched';
import computeBeneficiaryPayload from '@Application/Beneficiaries/Beneficiaries/Actions/computeBeneficiaryPayload';
import type { Computed } from '@Domain/Computed';
import type PlainDate from '@Domain/PlainDate';

/** The server sends a relation by id only (`forceObject: true`): an object, never a bare number. */
export type BeneficiaryStaffMemberPayload = {
	readonly id: number;
};

export type BeneficiaryPayload = BeneficiaryListItemPayload & {
	readonly nationalHealthIdentifier: string | null;
	// Detail-only fields: absent from `BeneficiaryListItemPayload`, never read from a list component.
	// The API always sends the key (object or `null`, never omitted, §7.6) — required here so a
	// factory or a caller that forgets it is caught at compile time rather than degrading silently to
	// `undefined` (which `BeneficiaryView` would then treat like `null`, masking the omission). The
	// front resolves the member's display name itself, from `staffMembersStore`: the id may point to a
	// member outside the caller's read scope (`staff_facility_filter`), the one case its name cannot
	// be resolved at all.
	readonly nursingStaffMember: BeneficiaryStaffMemberPayload | null;
	readonly homeCareStaffMember: BeneficiaryStaffMemberPayload | null;
};

export type ComputedBeneficiaryPayload = Computed<
	BeneficiaryPayload,
	{
		birthDate: PlainDate | null;
		archivedAt: Date | null;
		purgeScheduledAt: Date | null;
	}
>;

export type BeneficiaryFetchedAction = {
	type: 'BENEFICIARY_FETCHED';
	payload: ComputedBeneficiaryPayload;
	facilityId: number;
};

export default (payload: BeneficiaryPayload, facilityId: number): BeneficiaryFetchedAction => ({
	type: 'BENEFICIARY_FETCHED',
	payload: computeBeneficiaryPayload(payload),
	facilityId,
});
